import type { CapabilityType, PlanType } from '../models/Entitlements';
import type { ProductId } from '../models/ProductGrant';

export type CommercialCapabilityDepth = 'basic' | 'advanced';
export type CommercialPreviewKind = 'none' | 'synthetic' | 'safe_summary';
export type AdditionalCommercialGate =
  | 'protected_data'
  | 'source_authority'
  | 'approval'
  | 'separate_approver'
  | 'federation'
  | 'write_back'
  | 'repository_write'
  | 'ordinary_action'
  | 'ordinary_assignment'
  | 'ordinary_share'
  | 'ordinary_authoring'
  | 'ordinary_export';

export interface CommercialCapabilityPolicy {
  readonly allocationId: string;
  readonly capability: CapabilityType;
  readonly productId: ProductId;
  readonly depth: CommercialCapabilityDepth;
  readonly minimumPlan: 'free' | 'enterprise';
  readonly requiredProduct: ProductId | null;
  readonly previewKind: CommercialPreviewKind;
  readonly previewId: string | null;
  readonly mutating: boolean;
  readonly additionalGates: readonly AdditionalCommercialGate[];
  readonly enforcementOwner: string;
  readonly testCoverageId: string;
}

type Row = readonly [
  allocationId: string,
  capability: CapabilityType,
  productId: ProductId,
  depth: CommercialCapabilityDepth,
  mutating: boolean,
  additionalGates?: readonly AdditionalCommercialGate[],
];

const ROWS: readonly Row[] = [
  ['AT-01', 'atlas.chart.build', 'atlas', 'basic', true],
  ['AT-02', 'atlas.import.csv', 'atlas', 'basic', true],
  ['AT-03', 'atlas.view.structural', 'atlas', 'basic', false],
  ['AT-04', 'atlas.fields.packs', 'atlas', 'advanced', true, ['protected_data']],
  ['AT-05', 'atlas.sensitivity.tiers', 'atlas', 'advanced', true, ['protected_data']],
  ['AT-06', 'atlas.view.analytic', 'atlas', 'advanced', false, ['protected_data']],
  ['AT-07', 'atlas.scenario.model', 'atlas', 'advanced', true, ['approval']],
  ['AT-08', 'atlas.timetravel', 'atlas', 'advanced', false, ['protected_data']],
  ['AT-09', 'atlas.embed', 'atlas', 'advanced', false, ['ordinary_share']],

  ['PR-10', 'process.map.edit', 'process', 'basic', true],
  ['PR-11', 'process.inventory.view', 'process', 'basic', false],
  ['PR-12', 'process.extract.procedure', 'process', 'basic', true],
  ['PR-13', 'process.provenance.view', 'process', 'advanced', false],
  ['PR-14', 'process.extract.evidence', 'process', 'advanced', true],
  ['PR-15', 'process.extract.raci', 'process', 'advanced', true],
  ['PR-16', 'process.federation.read', 'process', 'advanced', false, ['federation']],
  ['PR-17', 'process.activity.log', 'process', 'advanced', false],
  ['PR-18', 'repository.control.drift.view', 'process', 'advanced', false],
  ['PR-19', 'repository.goldenThread.view', 'process', 'advanced', false],

  ['BL-20', 'blueprint.scan.import', 'blueprint', 'basic', true],
  ['BL-21', 'blueprint.inventory.view', 'blueprint', 'basic', false],
  ['BL-22', 'blueprint.lens.cost', 'blueprint', 'basic', false],
  ['BL-23', 'blueprint.lens.topology', 'blueprint', 'basic', false],
  ['BL-24', 'blueprint.findings.view', 'blueprint', 'advanced', false],
  ['BL-25', 'blueprint.exposure.path', 'blueprint', 'advanced', false],
  ['BL-26', 'blueprint.drift.view', 'blueprint', 'advanced', false],
  ['BL-27', 'blueprint.queue', 'blueprint', 'advanced', false],
  ['BL-28', 'blueprint.cost.waste_action', 'blueprint', 'advanced', true, ['ordinary_action']],
  ['BL-29', 'blueprint.ownership.assign', 'blueprint', 'advanced', true, ['ordinary_assignment']],
  ['BL-30', 'blueprint.scan.scheduled', 'blueprint', 'advanced', true],

  ['CO-31', 'compass.readiness.score', 'compass', 'basic', false],
  ['CO-32', 'compass.coverage.map', 'compass', 'basic', false],
  ['CO-33', 'compass.baseline.accept', 'compass', 'advanced', true, ['approval']],
  ['CO-34', 'compass.baseline.drift.view', 'compass', 'advanced', false],
  ['CO-35', 'compass.compliance.gap.resolve', 'compass', 'advanced', true, ['repository_write']],
  ['CO-36', 'compass.evidence.attach', 'compass', 'advanced', true, ['repository_write']],
  ['CO-37', 'compass.scenario.review', 'compass', 'advanced', true, ['approval']],
  ['CO-38', 'compass.scenario.approve', 'compass', 'advanced', true, ['approval', 'separate_approver']],
  ['CO-39', 'compass.scenario.apply', 'compass', 'advanced', true, ['approval', 'source_authority', 'repository_write']],
  ['CO-40', 'compass.flow_overlay.share', 'compass', 'advanced', true, ['ordinary_share']],
  ['CO-41', 'compass.spotlight.author', 'compass', 'advanced', true, ['ordinary_authoring']],
  ['CO-42', 'repository.control.drift.view', 'compass', 'advanced', false],
  ['CO-43', 'repository.goldenThread.view', 'compass', 'advanced', false],
  ['CO-44', 'compass.cost.waste_action', 'compass', 'advanced', true, ['ordinary_action']],
  ['CO-45', 'compass.ownership.assign', 'compass', 'advanced', true, ['ordinary_assignment']],

  ['CL-46', 'clinical.draw', 'clinical', 'basic', true],
  ['CL-47', 'clinical.text_to_genogram', 'clinical', 'basic', true],
  ['CL-48', 'clinical.export.image', 'clinical', 'basic', false, ['ordinary_export']],
  ['CL-49', 'clinical.health.view', 'clinical', 'advanced', false, ['protected_data']],
  ['CL-50', 'clinical.pattern.detect', 'clinical', 'advanced', false, ['protected_data']],
  ['CL-51', 'clinical.fields.packs', 'clinical', 'advanced', true, ['protected_data']],
  ['CL-52', 'clinical.report.generate', 'clinical', 'advanced', false, ['protected_data']],
  ['CL-53', 'clinical.perspectives', 'clinical', 'advanced', false, ['protected_data']],
  ['CL-54', 'clinical.export.court', 'clinical', 'advanced', false, ['protected_data', 'ordinary_export']],
];

export const COMMERCIAL_CAPABILITY_POLICIES: readonly CommercialCapabilityPolicy[] = ROWS.map(
  ([allocationId, capability, productId, depth, mutating, additionalGates = []]) => ({
    allocationId,
    capability,
    productId,
    depth,
    minimumPlan: depth === 'basic' ? 'free' : 'enterprise',
    requiredProduct: depth === 'advanced' ? productId : null,
    previewKind: depth === 'advanced' ? 'synthetic' : 'none',
    previewId: depth === 'advanced' ? `${productId}.advanced.v1` : null,
    mutating,
    additionalGates,
    enforcementOwner: `${productId}:ProductAccessService`,
    testCoverageId: `commercial-v3:${allocationId}`,
  }),
);

export function getCommercialCapabilityPolicy(
  capability: CapabilityType,
  productId?: ProductId,
): CommercialCapabilityPolicy | null {
  const matches = COMMERCIAL_CAPABILITY_POLICIES.filter((policy) => policy.capability === capability);
  if (matches.length === 1) return matches[0];
  if (!productId) return null;
  return matches.find((policy) => policy.productId === productId) ?? null;
}

export function listCommercialCapabilities(input?: {
  readonly productId?: ProductId;
  readonly depth?: CommercialCapabilityDepth;
}): CommercialCapabilityPolicy[] {
  return COMMERCIAL_CAPABILITY_POLICIES.filter((policy) =>
    (!input?.productId || policy.productId === input.productId)
    && (!input?.depth || policy.depth === input.depth));
}

export function planMeetsMinimum(planType: PlanType, minimum: 'free' | 'enterprise'): boolean {
  if (minimum === 'free') return true;
  return planType === 'enterprise';
}
